import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, message, Space, Popconfirm, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { getProducts, createProduct, updateProduct, deleteProduct } from '../services/api'; // 确保路径正确

const ProductList = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [searchForm] = Form.useForm();
    const [modalVisible, setModalVisible] = useState(false);
    const [modalForm] = Form.useForm();
    const [editingProduct, setEditingProduct] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]); // 新增：用于存储选中行的key

    const fetchProducts = async (page = pagination.current, pageSize = pagination.pageSize, params = {}) => {
        setLoading(true);
        try {
            const searchValues = await searchForm.getFieldsValue();
            const queryParams = { page, per_page: pageSize, ...searchValues, ...params };
            const response = await getProducts(queryParams);
            setProducts(response.data.products);
            setPagination({
                current: response.data.page || page,
                pageSize: response.data.per_page || pageSize,
                total: response.data.total,
            });
        } catch (error) {
            message.error('获取产品列表失败: ' + (error.response?.data?.error || error.message));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleTableChange = (newPagination) => {
        fetchProducts(newPagination.current, newPagination.pageSize);
    };

    const handleSearch = () => {
        fetchProducts(1); // 搜索时总是回到第一页
    };

    const resetSearch = () => {
        searchForm.resetFields();
        fetchProducts(1); // 重置时总是回到第一页
    };

    const showCreateModal = () => {
        setEditingProduct(null);
        modalForm.resetFields();
        setModalVisible(true);
    };

    const showEditModal = (product) => {
        setEditingProduct(product);
        modalForm.setFieldsValue(product);
        setModalVisible(true);
    };

    const handleModalOk = async () => {
        setModalLoading(true);
        try {
            const values = await modalForm.validateFields();
            if (editingProduct) {
                await updateProduct(editingProduct.id, values);
                message.success('产品更新成功');
            } else {
                await createProduct(values);
                message.success('产品创建成功');
            }
            setModalVisible(false);
            fetchProducts(editingProduct ? pagination.current : 1); // 如果是创建，回到第一页
        } catch (errorInfo) {
            if (errorInfo.response && errorInfo.response.data && errorInfo.response.data.error) {
                message.error(`操作失败: ${errorInfo.response.data.error}`);
            } else if (errorInfo.errorFields) {
                message.error('请检查表单输入!');
            }
            else {
                message.error('操作失败，请重试');
            }
            console.error('Modal操作失败:', errorInfo);
        } finally {
            setModalLoading(false);
        }
    };

    // 修改：handleDelete 现在接受一个id数组
    const handleDelete = async (ids) => {
        if (!ids || ids.length === 0) {
            message.warning('请选择要删除的产品');
            return;
        }
        try {
            // 修改：调用deleteProduct时传递包含product_ids的对象
            await deleteProduct({ product_ids: ids });
            message.success(`成功删除 ${ids.length} 个产品`);
            setSelectedRowKeys([]); // 删除成功后清空选择

            // 重新计算当前页码
            // 如果删除的是当前页的所有数据，并且当前不是第一页，则请求前一页
            const itemsOnCurrentPageAfterDeletion = products.length - ids.length;
            if (itemsOnCurrentPageAfterDeletion === 0 && pagination.current > 1) {
                fetchProducts(pagination.current - 1, pagination.pageSize);
            } else {
                // 否则刷新当前页（或者如果总数也导致页码变化，则去最后一页）
                // 简单起见，先刷新当前页， fetchProducts 会根据新的 total 来调整 pagination
                fetchProducts(pagination.current, pagination.pageSize);
            }
        } catch (error) {
            message.error('删除产品失败: ' + (error.response?.data?.error || error.message));
        }
    };

    const columns = [
        { title: 'ID', dataIndex: 'id', key: 'id', sorter: (a, b) => a.id - b.id },
        { title: '产品名称', dataIndex: 'name', key: 'name', sorter: (a, b) => a.name.localeCompare(b.name) },
        { title: '产品标识符', dataIndex: 'identifier', key: 'identifier', sorter: (a, b) => a.identifier.localeCompare(b.identifier) },
        { title: '描述', dataIndex: 'description', key: 'description', ellipsis: true },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => text ? new Date(text).toLocaleString() : '-',
            sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
        },
        {
            title: '操作',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Tooltip title="编辑">
                        <Button icon={<EditOutlined />} onClick={() => showEditModal(record)} />
                    </Tooltip>
                    <Popconfirm
                        title={`确定删除产品 "${record.name}" 吗?`}
                        onConfirm={() => handleDelete([record.id])} // 修改：传递数组
                        okText="确定"
                        cancelText="取消"
                    >
                        <Tooltip title="删除">
                            <Button icon={<DeleteOutlined />} danger />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    // 新增：处理表格行选择变化的函数
    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    // 新增：表格行选择配置
    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const hasSelected = selectedRowKeys.length > 0;

    return (
        <>
            <Form form={searchForm} layout="inline" onFinish={handleSearch} style={{ marginBottom: 16 }}>
                <Form.Item name="name" label="产品名称">
                    <Input placeholder="输入产品名称" allowClear />
                </Form.Item>
                <Form.Item name="identifier" label="产品标识符">
                    <Input placeholder="输入产品标识符" allowClear />
                </Form.Item>
                <Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                            搜索
                        </Button>
                        <Button onClick={resetSearch}>
                            重置
                        </Button>
                    </Space>
                </Form.Item>
            </Form>

            {/* 修改：将创建按钮和批量删除按钮放在一个Space中 */}
            <Space style={{ marginBottom: 16 }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={showCreateModal}>
                    创建产品
                </Button>
                {hasSelected && ( // 仅当有选中项时显示批量删除按钮
                    <Popconfirm
                        title={`确定删除选中的 ${selectedRowKeys.length} 个产品吗?`}
                        onConfirm={() => handleDelete(selectedRowKeys)}
                        okText="确定"
                        cancelText="取消"
                        disabled={!hasSelected} // 理论上外层 hasSelected 已保证，但双重保险
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            disabled={!hasSelected} // 禁用条件
                        >
                            批量删除 ({selectedRowKeys.length})
                        </Button>
                    </Popconfirm>
                )}
            </Space>

            <Table
                rowSelection={rowSelection} // 新增：启用行选择
                columns={columns}
                dataSource={products}
                rowKey="id"
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                scroll={{ x: 'max-content' }}
            />

            <Modal
                title={editingProduct ? '编辑产品' : '创建产品'}
                open={modalVisible}
                onOk={handleModalOk}
                onCancel={() => setModalVisible(false)}
                confirmLoading={modalLoading}
                destroyOnClose
            >
                <Form form={modalForm} layout="vertical" name="productForm">
                    <Form.Item
                        name="name"
                        label="产品名称"
                        rules={[{ required: true, message: '请输入产品名称!' }, { max: 100, message: '产品名称不能超过100个字符!' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="identifier"
                        label="产品标识符"
                        rules={[
                            { required: true, message: '请输入产品标识符!' },
                            { max: 50, message: '产品标识符不能超过50个字符!' },
                            { pattern: /^[a-zA-Z0-9_.-]*$/, message: '产品标识符只能包含字母、数字、下划线、点和短横线!' }
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label="描述"
                        rules={[{ max: 255, message: '描述信息不能超过255个字符!' }]}
                    >
                        <Input.TextArea rows={3} />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default ProductList;
